/**
 * @fileoverview Market-Aware API Hook
 * @task B.INV.BR - Brazilian market integration
 *
 * Provides a unified API interface that automatically switches between
 * US and Brazilian data based on the current market context.
 */

import { useMemo } from 'react';
import { useMarket, type Market } from '@contexts/MarketContext';
import {
  demoVendors as usVendors,
  demoCustomers as usCustomers,
  demoBills as usBills,
  demoInvoices as usInvoices,
  demoGLAccounts,
  type Vendor,
  type Customer,
  type Bill,
  type Invoice,
  type GLAccount,
  type BillStatus,
  type InvoiceStatus,
} from '../lib/demo-data';
import {
  brazilianVendors,
  brazilianCustomers,
  brazilianBills,
  brazilianInvoices,
  getBrazilianSummary,
} from '../lib/demo-data-br';

// ============================================================================
// Types
// ============================================================================

export interface DashboardStats {
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
  revenueGrowth: number;
  cashPosition: number;
  cashFlow: number;
  accountsReceivable: number;
  overdueInvoices: number;
  overdueInvoiceCount: number;
  averageDaysToCollect: number;
  accountsPayable: number;
  overdueBills: number;
  overdueBillCount: number;
  upcomingPayments: number;
  periodStart: string;
  periodEnd: string;
}

export interface AgingSummary {
  current: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  over90: number;
  total: number;
}

export interface CashFlowSummary {
  inflows: number;
  outflows: number;
  netFlow: number;
  openingBalance: number;
  closingBalance: number;
  period: string;
}

export interface ActivityItem {
  id: string;
  type: 'INVOICE_CREATED' | 'INVOICE_SENT' | 'INVOICE_PAID' | 'BILL_CREATED' | 'BILL_APPROVED' | 'BILL_PAID' | 'PAYMENT_RECEIVED' | 'PAYMENT_SENT';
  description: string;
  amount?: number;
  entityId: string;
  entityType: 'INVOICE' | 'BILL' | 'PAYMENT';
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================================================
// Configuration
// ============================================================================

const NETWORK_DELAY = {
  fast: 100,
  normal: 200,
  slow: 400,
};

async function simulateDelay(speed: keyof typeof NETWORK_DELAY = 'normal'): Promise<void> {
  const delay = NETWORK_DELAY[speed];
  await new Promise((resolve) => setTimeout(resolve, delay));
}

function getDateString(daysOffset: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().split('T')[0] ?? '';
}

// ============================================================================
// Market-Specific Data Selectors
// ============================================================================

// Use type assertions for market-specific data that extends base types
function getVendorsForMarket(market: Market): Vendor[] {
  return market === 'BR' ? (brazilianVendors as unknown as Vendor[]) : usVendors;
}

function getCustomersForMarket(market: Market): Customer[] {
  return market === 'BR' ? (brazilianCustomers as unknown as Customer[]) : usCustomers;
}

function getBillsForMarket(market: Market): Bill[] {
  if (market === 'BR') {
    // Transform Brazilian bills to match Bill interface (issueDate -> billDate, add lineItems)
    return brazilianBills.map((bill) => ({
      ...bill,
      billDate: bill.issueDate, // Map issueDate to billDate
      lineItems: [{ // Create a single line item from the flat structure
        id: `${bill.id}-li-1`,
        description: bill.description,
        quantity: 1,
        unitPrice: bill.amount,
        amount: bill.amount,
        glAccountId: bill.glAccountId,
        glAccountName: bill.category,
      }],
    })) as unknown as Bill[];
  }
  return usBills;
}

function getInvoicesForMarket(market: Market): Invoice[] {
  return market === 'BR' ? (brazilianInvoices as unknown as Invoice[]) : usInvoices;
}

// ============================================================================
// Market-Aware API Hook
// ============================================================================

export function useMarketApi() {
  const { market, config, formatCurrency, t } = useMarket();

  // Get current market data
  const vendors = useMemo(() => getVendorsForMarket(market), [market]);
  const customers = useMemo(() => getCustomersForMarket(market), [market]);
  const bills = useMemo(() => getBillsForMarket(market), [market]);
  const invoices = useMemo(() => getInvoicesForMarket(market), [market]);

  // Vendors API
  const vendorsApi = useMemo(() => ({
    async getVendors(params: { page?: number; limit?: number; search?: string } = {}): Promise<PaginatedResponse<Vendor>> {
      await simulateDelay('normal');

      let filtered = [...vendors];

      if (params.search) {
        const search = params.search.toLowerCase();
        filtered = filtered.filter(
          (v) =>
            v.name.toLowerCase().includes(search) ||
            v.email.toLowerCase().includes(search)
        );
      }

      const page = params.page || 1;
      const limit = params.limit || 10;
      const total = filtered.length;
      const totalPages = Math.ceil(total / limit);
      const start = (page - 1) * limit;
      const data = filtered.slice(start, start + limit);

      return { data, total, page, limit, totalPages };
    },

    async getVendorById(id: string): Promise<Vendor | null> {
      await simulateDelay('fast');
      return vendors.find((v) => v.id === id) ?? null;
    },

    async getAllVendors(): Promise<Vendor[]> {
      await simulateDelay('fast');
      return [...vendors];
    },
  }), [vendors]);

  // Customers API
  const customersApi = useMemo(() => ({
    async getCustomers(params: { page?: number; limit?: number; search?: string } = {}): Promise<PaginatedResponse<Customer>> {
      await simulateDelay('normal');

      let filtered = [...customers];

      if (params.search) {
        const search = params.search.toLowerCase();
        filtered = filtered.filter(
          (c) =>
            c.name.toLowerCase().includes(search) ||
            c.email.toLowerCase().includes(search)
        );
      }

      const page = params.page || 1;
      const limit = params.limit || 10;
      const total = filtered.length;
      const totalPages = Math.ceil(total / limit);
      const start = (page - 1) * limit;
      const data = filtered.slice(start, start + limit);

      return { data, total, page, limit, totalPages };
    },

    async getCustomerById(id: string): Promise<Customer | null> {
      await simulateDelay('fast');
      return customers.find((c) => c.id === id) ?? null;
    },

    async getAllCustomers(): Promise<Customer[]> {
      await simulateDelay('fast');
      return [...customers];
    },
  }), [customers]);

  // Bills API
  const billsApi = useMemo(() => ({
    async getBills(params: { page?: number; limit?: number; search?: string; status?: BillStatus } = {}): Promise<PaginatedResponse<Bill>> {
      await simulateDelay('normal');

      let filtered = [...bills];

      if (params.status) {
        filtered = filtered.filter((b) => b.status === params.status);
      }

      if (params.search) {
        const search = params.search.toLowerCase();
        filtered = filtered.filter(
          (b) =>
            b.billNumber.toLowerCase().includes(search) ||
            b.vendorName.toLowerCase().includes(search)
        );
      }

      // Sort by due date
      filtered.sort((a, b) => a.dueDate.localeCompare(b.dueDate));

      const page = params.page || 1;
      const limit = params.limit || 10;
      const total = filtered.length;
      const totalPages = Math.ceil(total / limit);
      const start = (page - 1) * limit;
      const data = filtered.slice(start, start + limit);

      return { data, total, page, limit, totalPages };
    },

    async getBillById(id: string): Promise<Bill | null> {
      await simulateDelay('fast');
      return bills.find((b) => b.id === id) ?? null;
    },

    async getBillsSummary(): Promise<{
      totalOutstanding: number;
      dueThisWeek: number;
      overdue: number;
      paidThisMonth: number;
      overdueCount: number;
      dueThisWeekCount: number;
    }> {
      await simulateDelay('fast');

      const today = getDateString(0);
      const weekFromNow = getDateString(7);
      const monthStart = new Date();
      monthStart.setDate(1);
      const monthStr = monthStart.toISOString().split('T')[0] ?? '';

      const outstandingBills = bills.filter(
        (b) => !['draft', 'paid'].includes(b.status)
      );
      const overdueBills = bills.filter((b) => b.status === 'overdue');
      const dueThisWeekBills = outstandingBills.filter(
        (b) => b.dueDate >= today && b.dueDate <= weekFromNow
      );
      const paidThisMonthBills = bills.filter(
        (b) => b.status === 'paid' && b.updatedAt >= monthStr
      );

      return {
        totalOutstanding: outstandingBills.reduce((sum, b) => sum + b.amount, 0),
        dueThisWeek: dueThisWeekBills.reduce((sum, b) => sum + b.amount, 0),
        overdue: overdueBills.reduce((sum, b) => sum + b.amount, 0),
        paidThisMonth: paidThisMonthBills.reduce((sum, b) => sum + b.amount, 0),
        overdueCount: overdueBills.length,
        dueThisWeekCount: dueThisWeekBills.length,
      };
    },
  }), [bills]);

  // Invoices API
  const invoicesApi = useMemo(() => ({
    async getInvoices(params: { page?: number; limit?: number; search?: string; status?: InvoiceStatus } = {}): Promise<PaginatedResponse<Invoice>> {
      await simulateDelay('normal');

      let filtered = [...invoices];

      if (params.status) {
        filtered = filtered.filter((inv) => inv.status === params.status);
      }

      if (params.search) {
        const search = params.search.toLowerCase();
        filtered = filtered.filter(
          (inv) =>
            inv.invoiceNumber.toLowerCase().includes(search) ||
            inv.customerName.toLowerCase().includes(search)
        );
      }

      // Sort by due date
      filtered.sort((a, b) => a.dueDate.localeCompare(b.dueDate));

      const page = params.page || 1;
      const limit = params.limit || 10;
      const total = filtered.length;
      const totalPages = Math.ceil(total / limit);
      const start = (page - 1) * limit;
      const data = filtered.slice(start, start + limit);

      return { data, total, page, limit, totalPages };
    },

    async getInvoiceById(id: string): Promise<Invoice | null> {
      await simulateDelay('fast');
      return invoices.find((inv) => inv.id === id) ?? null;
    },

    async getInvoicesSummary(): Promise<{
      totalOutstanding: number;
      expectedThisMonth: number;
      overdue: number;
      collectedThisMonth: number;
      overdueCount: number;
      expectedCount: number;
    }> {
      await simulateDelay('fast');

      const today = getDateString(0);
      const monthEnd = new Date();
      monthEnd.setMonth(monthEnd.getMonth() + 1);
      monthEnd.setDate(0);
      const monthEndStr = monthEnd.toISOString().split('T')[0] ?? '';
      const monthStart = new Date();
      monthStart.setDate(1);
      const monthStr = monthStart.toISOString().split('T')[0] ?? '';

      const outstandingInvoices = invoices.filter(
        (inv) => !['draft', 'paid', 'void'].includes(inv.status)
      );
      const overdueInvoices = invoices.filter((inv) => inv.status === 'overdue');
      const expectedThisMonthInvoices = outstandingInvoices.filter(
        (inv) => inv.dueDate >= today && inv.dueDate <= monthEndStr
      );
      const collectedThisMonthInvoices = invoices.filter(
        (inv) => inv.status === 'paid' && inv.updatedAt >= monthStr
      );

      return {
        totalOutstanding: outstandingInvoices.reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0),
        expectedThisMonth: expectedThisMonthInvoices.reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0),
        overdue: overdueInvoices.reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0),
        collectedThisMonth: collectedThisMonthInvoices.reduce((sum, inv) => sum + inv.paidAmount, 0),
        overdueCount: overdueInvoices.length,
        expectedCount: expectedThisMonthInvoices.length,
      };
    },
  }), [invoices]);

  // Dashboard API
  const dashboardApi = useMemo(() => ({
    async getDashboardStats(): Promise<DashboardStats> {
      await simulateDelay('normal');

      // Use market-specific summary for Brazilian market
      if (market === 'BR') {
        const brSummary = getBrazilianSummary();

        // Calculate outstanding amounts from actual data
        const outstandingBills = bills.filter(b => !['draft', 'paid'].includes(b.status));
        const overdueBills = bills.filter(b => b.status === 'overdue');
        const outstandingInvoices = invoices.filter(inv => !['draft', 'paid', 'void'].includes(inv.status));
        const overdueInvoices = invoices.filter(inv => inv.status === 'overdue');

        return {
          totalRevenue: brSummary.totalRevenue,
          totalExpenses: brSummary.totalExpenses,
          netIncome: brSummary.netIncome,
          revenueGrowth: brSummary.revenueGrowth,
          cashPosition: brSummary.cashPosition,
          cashFlow: brSummary.cashPosition * 0.2,
          accountsReceivable: outstandingInvoices.reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0),
          overdueInvoices: overdueInvoices.reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0),
          overdueInvoiceCount: overdueInvoices.length,
          averageDaysToCollect: 38,
          accountsPayable: outstandingBills.reduce((sum, b) => sum + b.amount, 0),
          overdueBills: overdueBills.reduce((sum, b) => sum + b.amount, 0),
          overdueBillCount: overdueBills.length,
          upcomingPayments: outstandingBills.slice(0, 3).reduce((sum, b) => sum + b.amount, 0),
          periodStart: getDateString(-30),
          periodEnd: getDateString(0),
        };
      }

      // US market stats (original)
      const billsSummary = await billsApi.getBillsSummary();
      const invoicesSummary = await invoicesApi.getInvoicesSummary();

      return {
        totalRevenue: 2170000.00,
        totalExpenses: 834000.00,
        netIncome: 1336000.00,
        revenueGrowth: 15.3,
        cashPosition: 610000.00,
        cashFlow: 125000.00,
        accountsReceivable: invoicesSummary.totalOutstanding,
        overdueInvoices: invoicesSummary.overdue,
        overdueInvoiceCount: invoicesSummary.overdueCount,
        averageDaysToCollect: 32,
        accountsPayable: billsSummary.totalOutstanding,
        overdueBills: billsSummary.overdue,
        overdueBillCount: billsSummary.overdueCount,
        upcomingPayments: billsSummary.dueThisWeek,
        periodStart: getDateString(-30),
        periodEnd: getDateString(0),
      };
    },

    async getARAgingSummary(): Promise<AgingSummary> {
      await simulateDelay('fast');

      const today = new Date();
      const outstandingInvoices = invoices.filter(
        (inv) => !['draft', 'paid', 'void'].includes(inv.status)
      );

      let current = 0;
      let days1to30 = 0;
      let days31to60 = 0;
      let days61to90 = 0;
      let over90 = 0;

      outstandingInvoices.forEach((inv) => {
        const dueDate = new Date(inv.dueDate);
        const daysPastDue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
        const balance = inv.amount - inv.paidAmount;

        if (daysPastDue <= 0) {
          current += balance;
        } else if (daysPastDue <= 30) {
          days1to30 += balance;
        } else if (daysPastDue <= 60) {
          days31to60 += balance;
        } else if (daysPastDue <= 90) {
          days61to90 += balance;
        } else {
          over90 += balance;
        }
      });

      return {
        current,
        days1to30,
        days31to60,
        days61to90,
        over90,
        total: current + days1to30 + days31to60 + days61to90 + over90,
      };
    },

    async getAPAgingSummary(): Promise<AgingSummary> {
      await simulateDelay('fast');

      const today = new Date();
      const outstandingBills = bills.filter(
        (b) => !['draft', 'paid'].includes(b.status)
      );

      let current = 0;
      let days1to30 = 0;
      let days31to60 = 0;
      let days61to90 = 0;
      let over90 = 0;

      outstandingBills.forEach((b) => {
        const dueDate = new Date(b.dueDate);
        const daysPastDue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysPastDue <= 0) {
          current += b.amount;
        } else if (daysPastDue <= 30) {
          days1to30 += b.amount;
        } else if (daysPastDue <= 60) {
          days31to60 += b.amount;
        } else if (daysPastDue <= 90) {
          days61to90 += b.amount;
        } else {
          over90 += b.amount;
        }
      });

      return {
        current,
        days1to30,
        days31to60,
        days61to90,
        over90,
        total: current + days1to30 + days31to60 + days61to90 + over90,
      };
    },

    async getCashFlowSummary(): Promise<CashFlowSummary> {
      await simulateDelay('fast');

      if (market === 'BR') {
        const brSummary = getBrazilianSummary();
        return {
          inflows: brSummary.totalRevenue * 0.22,
          outflows: brSummary.totalExpenses * 0.25,
          netFlow: brSummary.cashPosition * 0.15,
          openingBalance: brSummary.cashPosition * 0.85,
          closingBalance: brSummary.cashPosition,
          period: 'Janeiro 2026',
        };
      }

      return {
        inflows: 485000.00,
        outflows: 360000.00,
        netFlow: 125000.00,
        openingBalance: 485000.00,
        closingBalance: 610000.00,
        period: 'January 2026',
      };
    },

    async getRecentActivity(limit: number = 10): Promise<ActivityItem[]> {
      await simulateDelay('fast');

      if (market === 'BR') {
        // Brazilian activity items
        const activities: ActivityItem[] = [
          {
            id: 'act-br-001',
            type: 'INVOICE_PAID',
            description: 'Pagamento recebido de Petrobras S.A.',
            amount: 185000.00,
            entityId: 'inv-br-002',
            entityType: 'INVOICE',
            createdAt: getDateString(-1) + 'T14:30:00Z',
          },
          {
            id: 'act-br-002',
            type: 'BILL_APPROVED',
            description: 'Conta aprovada para Totvs S.A.',
            amount: 42500.00,
            entityId: 'bill-br-001',
            entityType: 'BILL',
            createdAt: getDateString(-1) + 'T11:15:00Z',
          },
          {
            id: 'act-br-003',
            type: 'INVOICE_SENT',
            description: 'NF-e enviada para Banco Itau S.A.',
            amount: 125000.00,
            entityId: 'inv-br-001',
            entityType: 'INVOICE',
            createdAt: getDateString(-2) + 'T16:45:00Z',
          },
          {
            id: 'act-br-004',
            type: 'BILL_PAID',
            description: 'Pagamento enviado para Localiza Rent a Car',
            amount: 28750.00,
            entityId: 'bill-br-003',
            entityType: 'BILL',
            createdAt: getDateString(-3) + 'T09:00:00Z',
          },
          {
            id: 'act-br-005',
            type: 'PAYMENT_RECEIVED',
            description: 'PIX recebido de Natura Cosmeticos',
            amount: 78500.00,
            entityId: 'inv-br-005',
            entityType: 'PAYMENT',
            createdAt: getDateString(-4) + 'T13:20:00Z',
          },
          {
            id: 'act-br-006',
            type: 'INVOICE_CREATED',
            description: 'Nova NF-e criada para Magazine Luiza',
            amount: 95000.00,
            entityId: 'inv-br-007',
            entityType: 'INVOICE',
            createdAt: getDateString(-5) + 'T10:30:00Z',
          },
          {
            id: 'act-br-007',
            type: 'BILL_CREATED',
            description: 'Nova conta recebida de AWS Brasil',
            amount: 18500.00,
            entityId: 'bill-br-002',
            entityType: 'BILL',
            createdAt: getDateString(-6) + 'T15:00:00Z',
          },
        ];
        return activities.slice(0, limit);
      }

      // US activity items (original)
      const activities: ActivityItem[] = [
        {
          id: 'act-001',
          type: 'INVOICE_PAID',
          description: 'Payment received from Wayne Enterprises',
          amount: 125000.00,
          entityId: 'inv-002',
          entityType: 'INVOICE',
          createdAt: getDateString(-1) + 'T14:30:00Z',
        },
        {
          id: 'act-002',
          type: 'BILL_APPROVED',
          description: 'Bill approved for Slack Technologies',
          amount: 22500.00,
          entityId: 'bill-011',
          entityType: 'BILL',
          createdAt: getDateString(-1) + 'T11:15:00Z',
        },
        {
          id: 'act-003',
          type: 'INVOICE_SENT',
          description: 'Invoice sent to Stark Industries',
          amount: 287500.00,
          entityId: 'inv-001',
          entityType: 'INVOICE',
          createdAt: getDateString(-2) + 'T16:45:00Z',
        },
        {
          id: 'act-004',
          type: 'BILL_PAID',
          description: 'Payment sent to Amazon Web Services',
          amount: 17850.00,
          entityId: 'bill-002',
          entityType: 'BILL',
          createdAt: getDateString(-3) + 'T09:00:00Z',
        },
        {
          id: 'act-005',
          type: 'PAYMENT_RECEIVED',
          description: 'Partial payment from Summit Energy Partners',
          amount: 92500.00,
          entityId: 'inv-005',
          entityType: 'PAYMENT',
          createdAt: getDateString(-4) + 'T13:20:00Z',
        },
        {
          id: 'act-006',
          type: 'INVOICE_CREATED',
          description: 'New invoice created for Beacon Properties',
          amount: 167500.00,
          entityId: 'inv-017',
          entityType: 'INVOICE',
          createdAt: getDateString(-5) + 'T10:30:00Z',
        },
        {
          id: 'act-007',
          type: 'BILL_CREATED',
          description: 'New bill received from Dell Technologies',
          amount: 45750.00,
          entityId: 'bill-008',
          entityType: 'BILL',
          createdAt: getDateString(-6) + 'T15:00:00Z',
        },
      ];

      return activities.slice(0, limit);
    },

    async getTopCustomers(limit: number = 5): Promise<{ customer: Customer; revenue: number }[]> {
      await simulateDelay('fast');

      // Calculate revenue per customer from invoices
      const customerRevenue = new Map<string, number>();

      invoices.forEach((inv) => {
        if (inv.status === 'paid' || inv.status === 'partial') {
          const current = customerRevenue.get(inv.customerId) || 0;
          customerRevenue.set(inv.customerId, current + inv.paidAmount);
        }
      });

      // Sort by revenue and get top customers
      const sortedEntries = Array.from(customerRevenue.entries())
        .sort(([, a], [, b]) => b - a)
        .slice(0, limit);

      return sortedEntries.map(([customerId, revenue]) => ({
        customer: customers.find((c) => c.id === customerId) || customers[0]!,
        revenue,
      }));
    },

    async getTopVendors(limit: number = 5): Promise<{ vendor: Vendor; spending: number }[]> {
      await simulateDelay('fast');

      // Calculate spending per vendor from bills
      const vendorSpending = new Map<string, number>();

      bills.forEach((bill) => {
        if (bill.status === 'paid') {
          const current = vendorSpending.get(bill.vendorId) || 0;
          vendorSpending.set(bill.vendorId, current + bill.amount);
        }
      });

      // Sort by spending and get top vendors
      const sortedEntries = Array.from(vendorSpending.entries())
        .sort(([, a], [, b]) => b - a)
        .slice(0, limit);

      return sortedEntries.map(([vendorId, spending]) => ({
        vendor: vendors.find((v) => v.id === vendorId) || vendors[0]!,
        spending,
      }));
    },
  }), [market, bills, invoices, customers, vendors, billsApi, invoicesApi]);

  // GL Accounts API (same for both markets)
  const glAccountsApi = useMemo(() => ({
    async getGLAccounts(): Promise<GLAccount[]> {
      await simulateDelay('fast');
      return [...demoGLAccounts];
    },

    async getGLAccountById(id: string): Promise<GLAccount | null> {
      await simulateDelay('fast');
      return demoGLAccounts.find((acc) => acc.id === id) ?? null;
    },

    async getExpenseAccounts(): Promise<GLAccount[]> {
      await simulateDelay('fast');
      return demoGLAccounts.filter((acc) => acc.type === 'expense');
    },

    async getRevenueAccounts(): Promise<GLAccount[]> {
      await simulateDelay('fast');
      return demoGLAccounts.filter((acc) => acc.type === 'revenue');
    },
  }), []);

  return {
    // Current market info
    market,
    config,
    formatCurrency,
    t,

    // APIs
    vendorsApi,
    customersApi,
    billsApi,
    invoicesApi,
    dashboardApi,
    glAccountsApi,

    // Direct data access (for components that need raw data)
    vendors,
    customers,
    bills,
    invoices,
  };
}

export default useMarketApi;
